import { isDefined, isNotDefined } from '@sgme/fp';
import { HStack } from '@sgme/ui';
import { clsx } from 'clsx';
import { formatDuration, intervalToDuration } from 'date-fns';
import { FormattedMessage } from 'react-intl';

import { useRemainingTime } from '@/App/tiles/shared/useRemainingTime.tsx';
import { SgButton } from '@/components/sg-design-system/components/input/SgButton';
import { useAppSelector } from '@/store/hooks.ts';
import type { DurationInSeconds } from '@/types/date.ts';

export type TimeProgressProps = {
  /** total RFS window duration in seconds */
  totalTime?: DurationInSeconds;

  /** remaining time in seconds */
  remainingTime?: DurationInSeconds;

  /** data-e2e for tests */
  'data-e2e'?: string;

  /** action to cancel the action */
  onCancel: () => void;

  clientsWay?: 'Buy' | 'Sell';
};

export function TimeProgress(props: TimeProgressProps) {
  const { showColoredPrices } = useAppSelector((state) => state.userPreferences);
  const { totalTime, remainingTime: externalRemainingTime, 'data-e2e': dataE2e, onCancel } = props;

  const remainingTime = useRemainingTime(externalRemainingTime);

  const progress =
    isDefined(remainingTime) && isDefined(totalTime) && totalTime > 0 ? (100 * remainingTime) / totalTime : undefined;

  // PENDING
  if (isNotDefined(progress)) {
    return (
      <HStack fullWidth horizontalAlignment="full" style={{ height: 20 }}>
        <div className="progress h-100 flex-grow-1" data-e2e={`${dataE2e}-progress`} data-progress={'pending'}>
          <div className="progress-bar progress-bar-striped progress-bar-animated bg-info w-100" role="progressbar" />
        </div>

        <SgButton
          type="button"
          purpose="discreet"
          className="btn btn-sm btn-flat-primary flex-grow-0"
          data-e2e={`${dataE2e}-cancel-button`}
          onClick={onCancel}
        >
          <i className="icon">close</i>
        </SgButton>
      </HStack>
    );
  }

  // PROGRESS
  const duration = intervalToDuration({ start: Date.now(), end: Date.now() + (remainingTime ?? 0) * 1000 });

  return (
    <HStack fullWidth horizontalAlignment="full" className="pt-2">
      <div
        className="progress progress-sm flex-grow-1"
        data-e2e={`${dataE2e}-progress`}
        data-progress={progress ?? 100}
      >
        <div
          className={clsx('progress-bar', {
            'bg-sell': props.clientsWay === 'Sell',
            'bg-buy': props.clientsWay === 'Buy',
            'bg-info': isDefined(progress) && (isNotDefined(props.clientsWay) || !showColoredPrices),
            'bg-secondary': isNotDefined(progress),
          })}
          role="progressbar"
          style={{ width: `${progress ?? 100}%` }}
          aria-valuenow={progress ?? 100}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <span className="progress-bar-label-over fs-6">
            {isDefined(remainingTime) ? formatDuration(duration) : <FormattedMessage id="progress.loading" />}
          </span>
        </div>
      </div>

      <SgButton type="button" purpose="flat" size="sm" data-e2e={`${dataE2e}-cancel-button`} onClick={onCancel}>
        <i className="icon">close</i>
      </SgButton>
    </HStack>
  );
}
